import { ArrowDown, ArrowUp, ArrowUpDown, Download, RefreshCw, Search, X } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import AdminPageLayout from "../../components/AdminPageLayout";
import { useTheme } from "../../contexts/ThemeContext";
import advisorAccountService, { IbkrSubaccount } from "../../services/advisorAccountService";
import AdminColumnFilterPopover from "./AdminProductsPage/components/AdminColumnFilterPopover";
import { useAdminColumnFilters } from "./AdminProductsPage/hooks/useAdminColumnFilters";

type CellValue = string | number | boolean | null | undefined;

const formatCellValue = (value: CellValue): string => {
  if (value === null || value === undefined || value === "") {
    return "N/A";
  }

  return String(value);
};

const formatArrayValue = (values: string[]): string => {
  return values.length > 0 ? values.join(", ") : "[]";
};

interface SubaccountColumn {
  key: string;
  label: string;
  className?: string;
  /** Raw value, used for type-aware sorting (numbers compare numerically). */
  value: (subaccount: IbkrSubaccount) => CellValue;
  /** Display text. Defaults to formatCellValue(value); search and filters match on this. */
  render?: (subaccount: IbkrSubaccount) => string;
}

const SUBACCOUNT_COLUMNS: SubaccountColumn[] = [
  { key: "id", label: "id", className: "font-mono", value: sub => sub.id, render: sub => sub.id },
  { key: "PrepaidCrypto-Z", label: "PrepaidCrypto-Z", value: sub => sub["PrepaidCrypto-Z"] },
  { key: "PrepaidCrypto-P", label: "PrepaidCrypto-P", value: sub => sub["PrepaidCrypto-P"] },
  { key: "brokerageAccess", label: "brokerageAccess", value: sub => sub.brokerageAccess },
  { key: "accountId", label: "accountId", className: "font-mono", value: sub => sub.accountId },
  { key: "accountVan", label: "accountVan", className: "font-mono", value: sub => sub.accountVan },
  { key: "accountTitle", label: "accountTitle", value: sub => sub.accountTitle },
  { key: "displayName", label: "displayName", value: sub => sub.displayName },
  { key: "accountAlias", label: "accountAlias", value: sub => sub.accountAlias },
  { key: "accountStatus", label: "accountStatus", className: "font-mono", value: sub => sub.accountStatus },
  { key: "currency", label: "currency", value: sub => sub.currency },
  { key: "type", label: "type", value: sub => sub.type },
  { key: "tradingType", label: "tradingType", value: sub => sub.tradingType },
  { key: "businessType", label: "businessType", value: sub => sub.businessType },
  { key: "category", label: "category", value: sub => sub.category },
  { key: "ibEntity", label: "ibEntity", value: sub => sub.ibEntity },
  { key: "faclient", label: "faclient", value: sub => sub.faclient },
  { key: "clearingStatus", label: "clearingStatus", value: sub => sub.clearingStatus },
  { key: "covestor", label: "covestor", value: sub => sub.covestor },
  { key: "noClientTrading", label: "noClientTrading", value: sub => sub.noClientTrading },
  { key: "trackVirtualFXPortfolio", label: "trackVirtualFXPortfolio", value: sub => sub.trackVirtualFXPortfolio },
  { key: "acctCustType", label: "acctCustType", value: sub => sub.acctCustType },
  { key: "desc", label: "desc", value: sub => sub.desc },
  {
    key: "parent.mmc",
    label: "parent.mmc",
    value: sub => sub.parent.mmc.join(", "),
    render: sub => formatArrayValue(sub.parent.mmc),
  },
  { key: "parent.accountId", label: "parent.accountId", className: "font-mono", value: sub => sub.parent.accountId },
  { key: "parent.isMParent", label: "parent.isMParent", value: sub => sub.parent.isMParent },
  { key: "parent.isMChild", label: "parent.isMChild", value: sub => sub.parent.isMChild },
  { key: "parent.isMultiplex", label: "parent.isMultiplex", value: sub => sub.parent.isMultiplex },
];

const cellText = (column: SubaccountColumn, subaccount: IbkrSubaccount): string =>
  column.render ? column.render(subaccount) : formatCellValue(column.value(subaccount));

const COLUMNS_BY_KEY = new Map(SUBACCOUNT_COLUMNS.map(column => [column.key, column]));

const EMPTY_SET = new Set<string>();

type SortDirection = "asc" | "desc";

const isMissingValue = (value: CellValue): boolean =>
  value === null || value === undefined || value === "";

/** Numbers compare numerically, booleans false-before-true, everything else naturally. */
const compareCellValues = (a: CellValue, b: CellValue): number => {
  if (typeof a === "number" && typeof b === "number") return a - b;
  if (typeof a === "boolean" && typeof b === "boolean") return Number(a) - Number(b);

  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" });
};

const AdvisorAccountsPage: React.FC = () => {
  const { theme } = useTheme();
  const isDarkMode = theme === "dark";

  const [ibkrSubaccounts, setIbkrSubaccounts] = useState<IbkrSubaccount[]>([]);
  const [subaccountsLoading, setSubaccountsLoading] = useState(false);
  const [subaccountsLoadProgress, setSubaccountsLoadProgress] = useState<{ loaded: number; total: number } | null>(null);
  const [subaccountsError, setSubaccountsError] = useState<string | null>(null);
  const [subaccountsLoaded, setSubaccountsLoaded] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [sort, setSort] = useState<{ key: string; direction: SortDirection } | null>(null);
  const {
    columnFilters,
    toggleFilter,
    clearFilter,
    clearAllFilters,
    activeFilterCount,
  } = useAdminColumnFilters();
  // Monotonic token: only the newest load may write state. Bumped on unmount so
  // an in-flight pagination run stops writing.
  const loadReqRef = useRef(0);

  // Options come from every loaded row, not the filtered subset, so unchecking a
  // value never removes it from its own popover.
  const filterOptions = useMemo(() => {
    const options: Record<string, string[]> = {};
    for (const column of SUBACCOUNT_COLUMNS) {
      const distinct = new Set<string>();
      for (const subaccount of ibkrSubaccounts) {
        distinct.add(cellText(column, subaccount));
      }
      options[column.key] = [...distinct].sort((a, b) =>
        a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" }),
      );
    }
    return options;
  }, [ibkrSubaccounts]);

  // "All" means "do not filter this column", so it clears the filter rather than
  // storing every value currently on screen. Storing them would freeze the filter to
  // the values seen so far, and rows whose value first appears on a later streamed
  // page would be dropped silently. An explicit tick stays strict by design: those
  // values, and only those, are wanted.
  const handleSelectAllFilter = useCallback(
    (columnKey: string) => {
      clearFilter(columnKey);
    },
    [clearFilter],
  );

  const visibleSubaccounts = useMemo(() => {
    // Every non-empty selection is applied as-is. Do not try to detect a selection
    // that "covers everything" and treat it as no filter: the option list is derived
    // from the loaded rows, so a refresh can swap it out from under a selection that
    // is still held, and any size- or coverage-based shortcut then disables a filter
    // the user never released.
    const activeFilterKeys = Object.keys(columnFilters).filter(key => (columnFilters[key]?.size ?? 0) > 0);
    const term = searchTerm.trim().toLowerCase();

    let rows = ibkrSubaccounts;

    if (activeFilterKeys.length > 0) {
      rows = rows.filter(subaccount =>
        activeFilterKeys.every(key => {
          const column = COLUMNS_BY_KEY.get(key);
          if (!column) return true;
          return columnFilters[key].has(cellText(column, subaccount));
        }),
      );
    }

    if (term) {
      rows = rows.filter(subaccount =>
        SUBACCOUNT_COLUMNS.some(column => cellText(column, subaccount).toLowerCase().includes(term)),
      );
    }

    const sortColumn = sort ? COLUMNS_BY_KEY.get(sort.key) : undefined;
    if (sort && sortColumn) {
      const direction = sort.direction === "asc" ? 1 : -1;
      // Copy first: the sorted view must not reorder the loaded rows themselves.
      rows = [...rows].sort((a, b) => {
        const aValue = sortColumn.value(a);
        const bValue = sortColumn.value(b);
        const aMissing = isMissingValue(aValue);
        const bMissing = isMissingValue(bValue);
        if (aMissing && bMissing) return 0;
        // Missing values sink to the bottom regardless of direction.
        if (aMissing) return 1;
        if (bMissing) return -1;
        return direction * compareCellValues(aValue, bValue);
      });
    }

    return rows;
  }, [ibkrSubaccounts, columnFilters, searchTerm, sort]);

  // asc -> desc -> unsorted, so the raw API order stays recoverable.
  const handleSort = useCallback((key: string) => {
    setSort(prev => {
      if (!prev || prev.key !== key) return { key, direction: "asc" };
      if (prev.direction === "asc") return { key, direction: "desc" };
      return null;
    });
  }, []);

  const resetView = useCallback(() => {
    setSearchTerm("");
    clearAllFilters();
    setSort(null);
  }, [clearAllFilters]);

  const isViewFiltered = searchTerm.trim().length > 0 || activeFilterCount > 0;

  const loadSubaccountsV2 = useCallback(async () => {
    const reqId = ++loadReqRef.current;
    const isStale = () => reqId !== loadReqRef.current;
    let replacedExistingRows = false;

    setSubaccountsLoading(true);
    setSubaccountsError(null);
    setSubaccountsLoadProgress(null);

    try {
      const result = await advisorAccountService.fetchAllSubaccounts(
        (loaded, total) => {
          if (!isStale()) {
            setSubaccountsLoadProgress({ loaded, total });
          }
        },
        isStale,
        batch => {
          if (isStale()) return;
          // Render pages as they land. The first page of a run replaces the
          // previous rows; later pages append. Keeping the old rows until the
          // first page arrives means a page-0 failure leaves prior data intact.
          if (replacedExistingRows) {
            setIbkrSubaccounts(prev => [...prev, ...batch]);
          } else {
            replacedExistingRows = true;
            setIbkrSubaccounts(batch);
            setSubaccountsLoaded(true);
          }
        },
      );
      if (isStale() || result === null) return;
      setIbkrSubaccounts(result);
      setSubaccountsLoaded(true);
    } catch (error: any) {
      if (!isStale()) {
        console.error("Failed to load IBKR subaccounts:", error);
        setSubaccountsError(error.response?.data?.error || error.message || "Failed to load IBKR subaccounts");
      }
    } finally {
      if (!isStale()) {
        setSubaccountsLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    const cached = advisorAccountService.getCachedSubaccounts();
    if (cached) {
      setIbkrSubaccounts(cached);
      setSubaccountsLoaded(true);
    }
    return () => {
      loadReqRef.current += 1;
    };
  }, []);

  return (
    <AdminPageLayout
      title="Advisor Accounts"
      description="View raw IBKR subaccounts returned by subaccountsV2"
      hideContentScroll
      actions={
        <button
          onClick={loadSubaccountsV2}
          disabled={subaccountsLoading}
          className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 transition-colors disabled:opacity-50"
        >
          {subaccountsLoading ? (
            <RefreshCw size={20} className="mr-2 animate-spin" />
          ) : subaccountsLoaded ? (
            <RefreshCw size={20} className="mr-2" />
          ) : (
            <Download size={20} className="mr-2" />
          )}
          {subaccountsLoading
            ? "Loading..."
            : subaccountsLoaded
              ? "Refresh IBKR Subaccounts"
              : "Load IBKR Subaccounts"}
        </button>
      }
    >
      <div className="h-full flex flex-col">
        <div className="flex-shrink-0">
          {subaccountsLoading && subaccountsLoadProgress && (
            <div className={`mb-4 p-4 rounded-lg ${isDarkMode ? "bg-rp-surface" : "bg-white"} shadow`}>
              <div className="flex items-center justify-between mb-2">
                <span className={`text-sm font-medium ${isDarkMode ? "text-rp-grey/70" : "text-gray-700"}`}>
                  Loading subaccounts... ({subaccountsLoadProgress.loaded}/{subaccountsLoadProgress.total})
                </span>
              </div>
              <div className={`w-full rounded-full h-2 ${isDarkMode ? "bg-rp-surface-sunken" : "bg-gray-200"}`}>
                <div
                  className="bg-teal-500 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${subaccountsLoadProgress.total > 0 ? (subaccountsLoadProgress.loaded / subaccountsLoadProgress.total) * 100 : 0}%` }}
                />
              </div>
            </div>
          )}

          {subaccountsLoading && !subaccountsLoadProgress && (
            <div className={`mb-4 p-4 rounded-lg ${isDarkMode ? "bg-rp-surface" : "bg-white"} shadow`}>
              <span className={`text-sm ${isDarkMode ? "text-rp-grey/70" : "text-gray-700"}`}>
                Fetching subaccounts...
              </span>
            </div>
          )}

          {subaccountsError && (
            <div className={`mb-4 p-3 rounded text-sm ${isDarkMode ? "bg-red-900/30 text-red-300" : "bg-red-50 text-red-700"}`}>
              {subaccountsError}
              {subaccountsLoadProgress && ibkrSubaccounts.length > 0 && (
                <span className="block mt-1">
                  Showing {ibkrSubaccounts.length} row(s) loaded before the failure.
                </span>
              )}
            </div>
          )}

          {ibkrSubaccounts.length > 0 && (
            <div className="mb-4 flex items-center gap-2 flex-wrap">
              <div className="relative w-full sm:w-80">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={event => setSearchTerm(event.target.value)}
                  placeholder="Search all columns..."
                  aria-label="Search subaccounts"
                  className="bg-rp-surface border border-rp-separator rounded-lg pl-10 pr-8 py-1.5 text-sm w-full focus:outline-none focus:border-rp-amber transition-all placeholder-rp-grey/40 text-rp-grey/80"
                />
                <Search className="absolute left-3 top-2.5 text-rp-grey/50 w-4 h-4" />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm("")}
                    aria-label="Clear search"
                    className="absolute right-2 top-2 text-rp-grey/50 hover:text-rp-grey"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>

              <span className="text-xs text-rp-grey/50">
                Showing {visibleSubaccounts.length} of {ibkrSubaccounts.length}
                {activeFilterCount > 0 && ` (${activeFilterCount} column filter${activeFilterCount === 1 ? "" : "s"})`}
              </span>

              {(isViewFiltered || sort) && (
                <button
                  onClick={resetView}
                  className="px-3 py-1.5 text-xs rounded-lg border border-rp-separator bg-rp-surface text-rp-grey/70 hover:text-rp-amber hover:border-rp-amber transition-colors"
                >
                  Reset view
                </button>
              )}
            </div>
          )}
        </div>

        {!subaccountsLoaded && ibkrSubaccounts.length === 0 ? (
          <div className={`flex-shrink-0 p-6 rounded-lg shadow ${isDarkMode ? "bg-rp-surface text-rp-grey/70" : "bg-white text-gray-700"}`}>
            Load IBKR subaccounts to view the full raw response as a flat table.
          </div>
        ) : ibkrSubaccounts.length === 0 ? (
          <div className={`flex-shrink-0 p-6 rounded-lg shadow ${isDarkMode ? "bg-rp-surface text-rp-grey/70" : "bg-white text-gray-700"}`}>
            No subaccounts found.
          </div>
        ) : visibleSubaccounts.length === 0 ? (
          <div className={`flex-shrink-0 p-6 rounded-lg shadow ${isDarkMode ? "bg-rp-surface text-rp-grey/70" : "bg-white text-gray-700"}`}>
            No subaccounts match your search or filters.
          </div>
        ) : (
          // Scrolls in both axes and is capped to the remaining viewport height, so the
          // horizontal scrollbar stays reachable instead of sitting below the last row.
          <div className={`flex-1 min-h-0 overflow-auto rounded-lg shadow ${isDarkMode ? "bg-rp-surface" : "bg-white"}`}>
            <table className="min-w-full table-auto">
              <thead>
                <tr>
                  {SUBACCOUNT_COLUMNS.map(column => {
                    const hasActiveFilter = (columnFilters[column.key]?.size ?? 0) > 0;
                    const isSorted = sort?.key === column.key;
                    return (
                      <th
                        key={column.key}
                        className={`sticky top-0 z-10 px-4 py-3 text-left text-xs font-medium uppercase tracking-wider whitespace-nowrap ${isDarkMode ? "bg-rp-surface-sunken text-rp-grey/70" : "bg-gray-100 text-gray-500"} ${hasActiveFilter ? "text-rp-amber" : ""}`}
                      >
                        <span className="inline-flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => handleSort(column.key)}
                            aria-label={`Sort by ${column.label}`}
                            className="inline-flex items-center gap-0.5 uppercase tracking-wider hover:text-rp-amber transition-colors select-none"
                          >
                            {column.label}
                            {!isSorted && <ArrowUpDown className="w-3 h-3 text-rp-grey/30" />}
                            {isSorted && sort?.direction === "asc" && <ArrowUp className="w-3 h-3 text-rp-amber" />}
                            {isSorted && sort?.direction === "desc" && <ArrowDown className="w-3 h-3 text-rp-amber" />}
                          </button>
                          <AdminColumnFilterPopover
                            columnKey={column.key}
                            options={filterOptions[column.key] ?? []}
                            label={column.label}
                            selectedValues={columnFilters[column.key] ?? EMPTY_SET}
                            onToggleValue={toggleFilter}
                            onSelectAll={handleSelectAllFilter}
                            onClear={clearFilter}
                          />
                        </span>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody className={`divide-y ${isDarkMode ? "divide-rp-separator" : "divide-gray-200"}`}>
                {visibleSubaccounts.map(subaccount => (
                  <tr key={subaccount.id} className={`${isDarkMode ? "hover:bg-rp-surface-raised text-rp-grey/70" : "hover:bg-gray-50 text-gray-900"}`}>
                    {SUBACCOUNT_COLUMNS.map(column => (
                      <td
                        key={column.key}
                        className={`px-4 py-2 whitespace-nowrap text-sm ${column.className ?? ""}`}
                      >
                        {cellText(column, subaccount)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminPageLayout>
  );
};

export default AdvisorAccountsPage;
